<template>
  <v-container>
    <!-- Loading state -->
    <v-container v-if="loading && !loaded">
      <v-row justify="center">
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-4">Loading member information...</p>
        </v-col>
      </v-row>
    </v-container>

    <!-- Error state -->
    <v-container v-else-if="!loading && !loaded && errorMessage">
      <v-row justify="center">
        <v-col cols="12" md="8">
          <v-alert type="error" prominent>
            <v-alert-title>Unable to Load Member Information</v-alert-title>
            <div>{{ errorMessage }}</div>
          </v-alert>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success state -->
    <v-container grid-list-xs v-if="loaded">
      <v-row class="pa-6 pb-0">
        <v-col cols="12" sm="12" md="6">
          <v-card elevation="2" class="stats-card">
            <v-card-title> Patient Info </v-card-title>
            <v-col cols="12" class="pl-0 pt-0">
              <v-list dense class="pt-0">
                <v-list-item>
                  <v-list-item-content>Patient Name</v-list-item-content>
                  <v-chip>
                    {{ patient_data.NAME }}
                  </v-chip>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>Patient ID</v-list-item-content>
                  <v-chip>
                    {{ patient_data.PATIENT_ID }}
                  </v-chip>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>Sex</v-list-item-content>
                  <v-chip>
                    {{ patient_data.SEX }}
                  </v-chip>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>DOB</v-list-item-content>
                  <v-chip>
                    {{ patient_data.BIRTH_DATE ? patient_data.BIRTH_DATE.split('T')[0] : 'N/A' }}
                  </v-chip>
                </v-list-item>
              </v-list>
            </v-col>
            <v-card-text class="pt-0 pb-0 body-1">{{ patient_data.ADDRESS }}</v-card-text>
            <v-card-text class="pt-0 pb-0 body-1"
              >{{ patient_data.CITY }}, {{ patient_data.STATE }}</v-card-text
            >
          </v-card>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <v-card elevation="2" class="stats-card">
            <v-card-title> Membership Info </v-card-title>
            <v-card-text class="pb-0 condensed body-1"
              >Provider: {{ patient_data.PCP_NAME }}</v-card-text
            >
            <v-card-text class="pb-0 condensed body-1"
              >Provider Location:
              {{
                patient_data.PCP_LOCATION_CODE ? patient_data.PCP_LOCATION_CODE : 'N/A'
              }}</v-card-text
            >
            <v-list dense>
              <v-list-item>
                <v-list-item-content>Effective Date</v-list-item-content>
                <v-chip>
                  {{ patient_data.ELIGIBLE_MONTH }}
                </v-chip>
              </v-list-item>
              <v-list-item
                v-if="
                  (insurance === 'Humana' || insurance === 'Careplus') &&
                  getPatientStatus() === 'error'
                "
              >
                <v-list-item-content>Term Date</v-list-item-content>
                <v-chip>
                  {{ patient_data.TERM_DATE }}
                </v-chip>
              </v-list-item>
            </v-list>
            <v-col cols="12" class="pl-0 pt-0"> </v-col>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="pa-6 pt-0">
        <v-col cols="12">
          <PatientConditions :patient="patientID" :insurance="insurance" />
        </v-col>

        <v-col cols="12">
          <institutional :patient="patientID" :insurance="insurance" />
        </v-col>

        <v-col cols="12">
          <professional :patient="patientID" :insurance="insurance" />
        </v-col>

        <v-col cols="12">
          <Rx :patient="patientID" :insurance="insurance" />
        </v-col>
        <v-col cols="12">
          <laboratory :patient="patientID" :insurance="insurance" />
        </v-col>
      </v-row>
    </v-container>
    <ClaimDetails
      :insurance="insurance"
      :claimID="claimDetailsID"
      v-model="claimDetailsDialog"
    />
  </v-container>
</template>

<script>
import axios from 'axios';
import PatientConditions from '../components/patientinfo/PatientConditions.vue';
import Institutional from '../components/patientinfo/Institutional.vue';
import Professional from '../components/patientinfo/Professional.vue';
import Rx from '../components/patientinfo/Rx.vue';
import Laboratory from '../components/patientinfo/Laboratory.vue';
import ClaimDetails from "@/components/claimDetails.vue";

export default {
  components: {
    ClaimDetails,
    PatientConditions,
    Institutional,
    Professional,
    Rx,
    Laboratory,
  },
  data() {
    return {
      insurance: '',
      claimDetailsID: '',
      claimDetailsDialog: false,
      loaded: false,
      loading: false,
      patientID: null,
      patient_data: {},
      errorMessage: '',
      user: '',
    };
  },
  mounted() {
    this.getUser();
  },
  methods: {
    async getUser() {
      await axios
        .get('/api/user')
        .then((response) => {
          this.user = response.data[0];
          this.patientID = this.user.memberId || this.user.member_id || this.user.MEMBER_ID || this.user.SUBSCRIBER_ID;
          this.insurance = Array.isArray(this.user.insurance) ? this.user.insurance[0] : this.user.insurance;
        })
        .catch(() => {
          this.$router.push('/login');
        });
      this.getPatient();
    },
    async getPatient() {
      this.loading = true;
      this.errorMessage = '';

      const insurance = Array.isArray(this.user.insurance)
        ? this.user.insurance[0]
        : this.user.insurance || this.insurance;

      if (!insurance) {
        this.errorMessage = 'Insurance information not found for this user.';
        this.loading = false;
        return;
      }

      const memberId = this.user.memberId || this.user.member_id || this.user.MEMBER_ID || this.user.SUBSCRIBER_ID || this.patientID;

      if (!memberId) {
        this.errorMessage = 'Member ID not found for this user. Please contact support.';
        this.loading = false;
        return;
      }

      await axios
        .get(`/api/patient/modal/insurance=${insurance}/${memberId}`)
        .then((response) => {
          const data = response.data;
          if (Array.isArray(data) && data.length > 0 && data[0]) {
            this.patient_data = data[0];
            this.loaded = true;
          } else if (data && !Array.isArray(data) && Object.keys(data).length > 0) {
            this.patient_data = data;
            this.loaded = true;
          } else {
            this.errorMessage = `No patient data found for Member ID: ${memberId}`;
          }
          this.loading = false;
        })
        .catch((error) => {
          const resp = error.response || {};
          const status = resp.status;
          const msg = resp.data || error.message || 'Error loading patient data';
          this.errorMessage = status ? ('Error ' + status + ': ' + msg) : String(msg);
          this.loading = false;
        });
    },
    getPatientStatus() {
      if (this.insurance === 'Humana') {
        if (this.patient_data.DECEASED != null) return 'black';
        if (
          this.patient_data.STATUS_CODE === null ||
          this.patient_data.STATUS_CODE === '1' ||
          this.patient_data.STATUS_CODE === '2'
        ) return 'success';
        return 'error';
      }
      if (this.insurance === 'Careplus') {
        const today = new Date().toISOString();
        if (this.patient_data.Enrollment_Date < today && this.patient_data.Disenrollment_Date == null) {
          return 'success';
        }
        return 'error';
      }
    },
  },
};
</script>

<style>
.v-dialog--fullscreen {
  overflow-x: hidden !important;
}

.stats-card {
  min-height: 285px;
}
</style>
